import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { downloadDataUrl } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import EnrollmentForm from "@/components/EnrollmentForm";
import InstallAppButton from "@/components/InstallAppButton";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ChangeEmailDialog from "@/components/ChangeEmailDialog";
import { StudentCalendar } from "@/components/StudentCalendar";
import { toast } from "sonner";
import { LogOut, CalendarDays, MapPin, Clock, CheckCircle2, Lock, Unlock, Download, CreditCard, Ticket, FileSignature, GraduationCap, History, BadgeEuro, RefreshCw, BadgeCheck } from "lucide-react";

const LOGO = "https://www.areteteatro.com/wp-content/uploads/2020/03/logo-arete.png";

// Testo istruzioni pagamento Satispay (facilmente modificabile)
const SATISPAY_INSTRUCTIONS = "Paga con Satispay secondo le indicazioni della direzione, poi conferma qui il pagamento";

const STATUS = {
  locked: { l: "In attesa di sblocco", c: "bg-muted text-muted-foreground" },
  unlocked: { l: "Iscrizione sbloccata", c: "bg-primary/15 text-primary" },
  form_signed: { l: "Modulo firmato", c: "bg-blue-500/15 text-blue-600" },
  partially_paid: { l: "Prima rata pagata", c: "bg-amber-500/15 text-amber-600" },
  enrolled: { l: "Iscritto ✓", c: "bg-green-500/15 text-green-600" },
};

export default function Dashboard() {
  const { user, logout, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [paying, setPaying] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [phoneSlot, setPhoneSlot] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [inPersonOpen, setInPersonOpen] = useState(false);
  const [ipMethod, setIpMethod] = useState("contanti");
  const [ipPlan, setIpPlan] = useState("single");

  const load = async (y) => {
    const { data: d } = await api.get("/me/dashboard", { params: y ? { year: y } : {} });
    setData(d);
    setYear(d.year);
  };
  useEffect(() => {
    localStorage.removeItem("arete_pending_slot");
    load();
  }, []);

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(year).catch(() => {}); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, [year]);

  const book = async (slot_id, phone) => {
    const moving = (data.bookings || []).length > 0;
    try {
      await api.post("/bookings", { slot_id, phone });
      if (phone && user && !user.phone) setUser({ ...user, phone });
      toast.success(moving ? "Prenotazione spostata! Ti abbiamo inviato una email di conferma." : "Lezione di prova prenotata! Ti abbiamo inviato una email di conferma.");
      load(year);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const onBookClick = (slot_id) => {
    if (user?.phone) return book(slot_id, user.phone);
    setPhoneInput("");
    setPhoneSlot(slot_id);
  };

  const confirmPhoneBook = async () => {
    if (!phoneInput.trim()) return toast.error("Inserisci un numero di telefono");
    const sid = phoneSlot;
    setPhoneSlot(null);
    await book(sid, phoneInput.trim());
  };

  const downloadPdf = async () => {
    try {
      const { data: d } = await api.get("/enrollment/pdf", { params: { year } });
      if (!d.signed_pdf) return toast.error("Modulo non disponibile");
      downloadDataUrl(d.signed_pdf, `modulo-iscrizione-${(year || "").replace("/", "-")}.pdf`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const pay = async (plan) => {
    setPaying(true);
    try {
      const { data: d } = await api.post("/payments/checkout", { plan, year, origin_url: window.location.origin });
      window.location.href = d.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setPaying(false); }
  };
  void pay;

  const declareInPerson = async (declaredPaid) => {
    const effPlan = enr.status === "partially_paid" ? "installments" : ipPlan;
    setPaying(true);
    try {
      await api.post("/payments/in-person", { plan: effPlan, method: ipMethod, year, declared_paid: !!declaredPaid });
      toast.success(declaredPaid ? "Grazie! Abbiamo avvisato la direzione: verificheranno e registreranno il pagamento." : "Perfetto! Recati in sede per il pagamento: sarà la direzione a registrarlo.");
      load(year);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  const requestUnlock = async () => {
    setRequesting(true);
    try { await api.post("/enrollment/request-unlock", null, { params: { year } }); toast.success("Richiesta inviata! La direzione la riceverà e sbloccherà la tua iscrizione."); load(year); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRequesting(false); }
  };

  const submitCode = async () => {
    setCodeErr("");
    setRequesting(true);
    try {
      const { data: updatedEnr } = await api.post("/enrollment/unlock-with-code", { code: code.trim(), year });
      // Aggiornamento immediato dallo stato restituito dal backend: la UI si sblocca subito
      setData((prev) => (prev ? { ...prev, enrollment: updatedEnr } : prev));
      setCodeOpen(false); setCode("");
      toast.success("Iscrizione sbloccata! Ora puoi compilare il modulo di iscrizione.");
      // Ricarico l'intero profilo utente e la dashboard (enrollment + prenotazioni + slot)
      try { const { data: me } = await api.get("/auth/me"); setUser(me); } catch {}
      try { await load(year); } catch {}
      setTimeout(() => {
        document.getElementById("enrollment-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (err) {
      setCodeErr(apiError(err.response?.data?.detail) || "Codice non valido. Contatta la direzione per riceverlo.");
    } finally { setRequesting(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const enr = data.enrollment;
  const cfg = data.year_config;
  const st = STATUS[enr?.status] || STATUS.locked;
  const isCurrent = data.is_current;
  const bookedIds = new Set(data.bookings.map((b) => b.slot_id));

  return (
    <div className="min-h-screen bg-background">
      <header style={{ paddingTop: "env(safe-area-inset-top)" }} className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0"><img src={LOGO} alt="Areté Teatro" className="h-9 w-9 rounded-full bg-white p-0.5 object-contain shrink-0" /><span className="text-lg font-display text-white whitespace-nowrap">Areté Teatro</span></div>
          <div className="flex items-center gap-1 sm:gap-3">
            <span className="text-sm text-white/70 hidden md:block">Ciao, {user?.name?.split(" ")[0]}</span>
            <Button data-testid="refresh-dashboard" variant="ghost" size="sm" onClick={() => load(year)} className="text-white hover:bg-white/10 hover:text-white"><RefreshCw size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Aggiorna</span></Button>
            <ChangeEmailDialog className="text-white hover:bg-white/10 hover:text-white" />
            <ChangePasswordDialog className="text-white hover:bg-white/10 hover:text-white" />
            <InstallAppButton className="text-white hover:bg-white/10 hover:text-white" />
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10 hover:text-white"><LogOut size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Esci</span></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10 fade-up">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl">La tua area personale</h1>
            <p className="text-muted-foreground mt-2 text-lg">Anno scolastico <strong>{year}</strong>{isCurrent && " (corrente)"}.</p>
          </div>
          {data.years.length > 1 && (
            <div className="w-full sm:w-56">
              <label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1"><History size={13} /> Anno scolastico</label>
              <Select value={year} onValueChange={(y) => load(y)}>
                <SelectTrigger data-testid="year-select" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {data.years.map((y) => <SelectItem key={y} value={y}>{y}{y === data.current_year ? " (corrente)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Status widget */}
        <Card className="p-6 bg-card border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Stato iscrizione {year}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className={`${st.c} border-0`} data-testid="enrollment-status">{st.l}</Badge>
              {enr?.course && <span className="text-sm flex items-center gap-1.5"><GraduationCap size={15} className="text-primary" /> Corso: <strong>{enr.course}</strong></span>}
              {enr?.amount_paid > 0 && <span className="text-sm text-muted-foreground">Pagato: €{enr.amount_paid}{enr.amount_due > 0 && ` · Residuo: €${enr.amount_due}`}</span>}
              {enr?.arci_paid && <Badge className="bg-green-500/15 text-green-600 border-0" data-testid="arci-badge"><BadgeCheck size={13} className="mr-1" /> Tessera Arci pagata</Badge>}
            </div>
            {isCurrent && (!enr || !enr.unlocked) && (
              enr?.unlock_requested ? (
                <div className="mt-3 flex items-start gap-2 text-sm text-primary" data-testid="unlock-requested-msg">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                  <span>La tua richiesta è stata presa in carico. Attendi che la direzione sblocchi la tua iscrizione.</span>
                </div>
              ) : (
                <div className="mt-3 space-y-2 max-w-xl">
                  <p className="text-sm text-muted-foreground">Hai fatto la lezione di prova e vuoi continuare? Clicca qui per richiedere l'iscrizione al corso. Se non hai ancora fatto la lezione di prova gratuita, ti consigliamo di farla prima di procedere.</p>
                  <Button size="sm" data-testid="request-unlock-button" onClick={() => { setCode(""); setCodeErr(""); setCodeOpen(true); }} className="bg-primary text-primary-foreground hover:bg-primary/90"><Unlock size={15} className="mr-1.5" /> Voglio iscrivermi al corso</Button>
                </div>
              )
            )}
          </div>
        </Card>

        {/* Calendario lezioni del proprio corso */}
        {enr?.course && <StudentCalendar key={`${year}|${enr.course}`} year={year} isCurrent={isCurrent} />}

        {/* Trials - solo se ha una prenotazione E l'iscrizione non è ancora sbloccata */}
        {data.bookings.length > 0 && !enr?.unlocked && (
          <section>
            <div className="mb-4">
            <div className="flex items-center gap-2"><Ticket className="text-primary" size={20} /><h2 className="text-2xl">Lezioni di prova gratuite</h2></div>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">Qui puoi scegliere e gestire la tua lezione di prova gratuita.</p>
          </div>
            {data.bookings.length > 0 && (
              <Card className="p-4 mb-4 bg-primary/5 border-primary/30 flex items-center gap-3" data-testid="current-booking-note">
                <CheckCircle2 size={18} className="text-primary shrink-0" />
                <p className="text-sm">Hai prenotato una lezione di prova per <strong className="capitalize">{new Date(data.bookings[0].date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} alle {data.bookings[0].time}</strong>. Puoi <strong>spostarla</strong> scegliendo un'altra data qui sotto (ne è ammessa una sola).</p>
              </Card>
            )}
            {data.slots.length === 0 ? (
              <Card className="p-8 bg-card border-border text-center text-muted-foreground">Nessuna data disponibile al momento. Torna presto!</Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.slots.map((s) => {
                  const booked = bookedIds.has(s.id);
                  const full = s.closed || s.booked_count >= s.capacity;
                  const hasBooking = data.bookings.length > 0;
                  const label = booked ? "Prenotato ✓" : full ? "Prenotazioni chiuse" : hasBooking ? "Sposta qui" : "Prenota prova";
                  return (
                    <Card key={s.id} className={`p-5 bg-card border-border hover:border-primary/40 transition-colors ${booked ? "border-primary/50" : ""}`}>
                      <div className="flex items-center gap-2 text-primary mb-2"><CalendarDays size={16} /><span className="font-medium text-foreground capitalize">{new Date(s.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</span></div>
                      <p className="text-sm text-muted-foreground flex items-center gap-2 mb-1"><Clock size={14} /> {s.time}</p>
                      {s.location && <p className="text-sm text-muted-foreground flex items-center gap-2 mb-3"><MapPin size={14} /> {s.location}</p>}
                      <div className="mb-4" />
                      <Button data-testid={`book-slot-${s.id}`} disabled={booked || full} onClick={() => onBookClick(s.id)} variant={hasBooking && !booked && !full ? "outline" : "default"} className={`w-full disabled:opacity-50 ${booked || !hasBooking ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}>
                        {label}
                      </Button>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Enrollment */}
        {(!isCurrent || (enr && enr.unlocked)) && (
        <section id="enrollment-section">
          <div className="flex items-center gap-2 mb-4"><FileSignature className="text-primary" size={20} /><h2 className="text-2xl">Iscrizione anno {year}</h2></div>

          {!isCurrent ? (
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground mb-4">Stai visualizzando uno <strong>storico</strong>. Stato: <Badge className={`${st.c} border-0`}>{st.l}</Badge></p>
              {["form_signed", "partially_paid", "enrolled"].includes(enr?.status) && (
                <Button variant="outline" onClick={downloadPdf}><Download size={16} className="mr-2" /> Scarica modulo di quell'anno</Button>
              )}
            </Card>
          ) : enr.status === "unlocked" ? (
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground mb-6">Compila il modulo digitale e firmalo per procedere con il pagamento.</p>
              <EnrollmentForm userName={user?.name} year={year} courses={cfg.courses} onDone={() => load(year)} />
            </Card>
          ) : (
            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="p-6 bg-card border-border">
                <h3 className="text-xl mb-2">Modulo firmato</h3>
                <p className="text-muted-foreground text-sm mb-4">Il tuo modulo di iscrizione è stato firmato e registrato.</p>
                <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-button"><Download size={16} className="mr-2" /> Scarica modulo firmato</Button>
              </Card>

              <Card className="p-6 bg-card border-border">
                <h3 className="text-xl mb-1 flex items-center gap-2"><CreditCard size={18} className="text-primary" /> Pagamento</h3>
                {enr.status === "enrolled" ? (
                  <p className="text-green-600 mt-3">Iscrizione completata e pagata. Grazie!</p>
                ) : (() => {
                  const effPlan = enr.status === "partially_paid" ? "installments" : ipPlan;
                  const rataLabel = enr.status === "partially_paid" ? "seconda rata" : (effPlan === "single" ? "rata unica" : "prima rata");
                  const amount = effPlan === "single" ? cfg.full_amount : cfg.installment_amount;
                  const yl = year ? `${year.split("/")[0]}-${year.split("/")[1].slice(2)}` : "";
                  const causale = `${user?.name || "Nome Cognome"} contributo ${rataLabel} corso teatro ${yl}`;
                  const req = enr.in_person_request;
                  const ML = { carta: "Bancomat/Carta", contanti: "Contanti", bonifico: "Bonifico", satispay: "Satispay" };
                  return (
                    <div className="space-y-4 mt-3" data-testid="payment-panel">
                      {req && (
                        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-700" data-testid="in-person-banner">
                          {req.declared_paid
                            ? <>Hai segnalato di aver pagato con <strong>{ML[req.method] || req.method}</strong>. In attesa di verifica e registrazione da parte della direzione.</>
                            : <>Hai scelto di pagare in sede ({ML[req.method]}). Sarà la direzione a registrare il pagamento.</>}
                        </div>
                      )}
                      {enr.status !== "partially_paid" ? (
                        <div>
                          <p className="text-sm font-medium mb-1.5">Scegli il piano</p>
                          <div className="grid grid-cols-2 gap-2">
                            <Button type="button" variant={ipPlan === "single" ? "default" : "outline"} className={ipPlan === "single" ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""} data-testid="plan-single" onClick={() => setIpPlan("single")}>Unica rata · €{cfg.full_amount}</Button>
                            <Button type="button" variant={ipPlan === "installments" ? "default" : "outline"} className={ipPlan === "installments" ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""} data-testid="plan-installments" onClick={() => setIpPlan("installments")}>Due rate · €{cfg.installment_amount} x2</Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Hai pagato la prima rata. Salda la <strong>seconda rata</strong> (€{cfg.installment_amount}).</p>
                      )}
                      <div>
                        <p className="text-sm font-medium mb-1.5">Come vuoi pagare?</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[{ v: "contanti", l: "Contanti" }, { v: "carta", l: "Bancomat / Carta" }, { v: "bonifico", l: "Bonifico" }, { v: "satispay", l: "Satispay" }].map((m) => (
                            <Button key={m.v} type="button" variant={ipMethod === m.v ? "default" : "outline"} className={`min-w-0 h-auto py-2 px-1.5 text-xs leading-tight !whitespace-normal break-words text-center ${ipMethod === m.v ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`} data-testid={`method-${m.v}`} onClick={() => setIpMethod(m.v)}>{m.l}</Button>
                          ))}
                        </div>
                      </div>
                      {ipMethod === "bonifico" ? (
                        <div className="rounded-lg border border-border bg-secondary/40 p-4 space-y-2 text-sm" data-testid="bonifico-box">
                          <p className="font-medium text-foreground">Effettua un bonifico di €{amount} con questi dati:</p>
                          <div><span className="text-muted-foreground">Beneficiario:</span> <strong>ARETE' APS</strong></div>
                          <div><span className="text-muted-foreground">IBAN:</span> <strong className="break-all">IT91G0503401005000000009879</strong></div>
                          <div><span className="text-muted-foreground">Causale:</span> <strong data-testid="bonifico-causale">{causale}</strong></div>
                          <Button data-testid="declare-bonifico" disabled={paying} onClick={() => declareInPerson(true)} className="w-full mt-1 bg-primary text-primary-foreground hover:bg-primary/90">Ho effettuato il bonifico</Button>
                        </div>
                      ) : ipMethod === "satispay" ? (
                        <div className="rounded-lg border border-border bg-secondary/40 p-4 space-y-2 text-sm" data-testid="satispay-box">
                          <p className="text-foreground">{SATISPAY_INSTRUCTIONS} (€{amount}).</p>
                          <Button data-testid="declare-satispay" disabled={paying} onClick={() => declareInPerson(true)} className="w-full mt-1 bg-primary text-primary-foreground hover:bg-primary/90">Ho pagato con Satispay</Button>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-border bg-secondary/40 p-4 space-y-2 text-sm" data-testid="inperson-box">
                          <p>Recati in <strong>sede</strong> per pagare {ipMethod === "contanti" ? "in contanti" : "con bancomat/carta"} (€{amount}). Sarà la direzione a registrare il pagamento.</p>
                          <Button data-testid="declare-inperson" disabled={paying} onClick={() => declareInPerson(false)} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">Comunica alla direzione</Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Card>
            </div>
          )}
        </section>
        )}

        {data.course_history?.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4"><GraduationCap className="text-primary" size={20} /><h2 className="text-2xl">I tuoi corsi</h2></div>
            <Card className="bg-card border-border divide-y divide-border" data-testid="course-history">
              {data.course_history.map((h, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3" data-testid={`course-hist-${i}`}>
                  <span className="text-sm flex items-center gap-2"><GraduationCap size={15} className="text-primary" /> {h.course}</span>
                  <Badge className="bg-muted text-muted-foreground border-0">{h.year}{h.year === data.current_year ? " (corrente)" : ""}</Badge>
                </div>
              ))}
            </Card>
          </section>
        )}

        {data.payments.length > 0 && (
          <section>
            <h2 className="text-2xl mb-4">Storico pagamenti {year}</h2>
            <Card className="bg-card border-border divide-y divide-border">
              {data.payments.map((p) => (
                <div key={p.session_id} className="flex items-center justify-between px-5 py-3">
                  <div><p className="text-sm">{p.label}</p><p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString("it-IT")}</p></div>
                  <div className="text-right"><p className="text-sm">€{p.amount}</p><Badge className={p.payment_status === "paid" ? "bg-green-500/15 text-green-600 border-0" : "bg-muted text-muted-foreground border-0"}>{p.payment_status === "paid" ? "Pagato" : "In attesa"}</Badge></div>
                </div>
              ))}
            </Card>
          </section>
        )}
      </main>

      <Dialog open={!!phoneSlot} onOpenChange={(o) => { if (!o) setPhoneSlot(null); }}>
        <DialogContent className="max-w-sm" data-testid="phone-dialog">
          <DialogHeader>
            <DialogTitle>Conferma la prenotazione</DialogTitle>
            <DialogDescription>Inserisci un numero di telefono, usato esclusivamente per la logistica di contatto della lezione.</DialogDescription>
          </DialogHeader>
          <div><Label>Telefono <span className="text-primary">*</span></Label><Input data-testid="phone-dialog-input" type="tel" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} className="mt-1.5 bg-white" placeholder="333 1234567" autoFocus /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhoneSlot(null)}>Annulla</Button>
            <Button data-testid="phone-dialog-confirm" onClick={confirmPhoneBook} className="bg-primary text-primary-foreground hover:bg-primary/90">Conferma prenotazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={codeOpen} onOpenChange={(o) => { setCodeOpen(o); if (!o) setCodeErr(""); }}>
        <DialogContent className="max-w-md" data-testid="unlock-code-dialog">
          <DialogHeader>
            <DialogTitle>Richiedi l'iscrizione</DialogTitle>
            <DialogDescription>Hai partecipato alla lezione di prova e vuoi continuare il tuo percorso con noi? Inserisci il codice fornito dalla direzione al termine della lezione di prova per procedere.</DialogDescription>
          </DialogHeader>
          <div>
            <Input data-testid="unlock-code-input" value={code} onChange={(e) => { setCode(e.target.value); setCodeErr(""); }} placeholder="Inserisci il codice" className="bg-white" autoFocus onKeyDown={(e) => { if (e.key === "Enter" && code.trim()) submitCode(); }} />
            {codeErr && <p data-testid="unlock-code-error" className="text-sm text-destructive mt-2">{codeErr}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="unlock-code-cancel" onClick={() => setCodeOpen(false)}>Annulla</Button>
            <Button data-testid="unlock-code-confirm" disabled={requesting || !code.trim()} onClick={submitCode} className="bg-primary text-primary-foreground hover:bg-primary/90">{requesting ? "Verifico…" : "Conferma"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
